import { Button, Loader, Menu } from "@mantine/core";
import { showNotification } from "@mantine/notifications";
import jsPDF from "jspdf";
import "jspdf-autotable";
import { useState } from "react";
import * as XLSX from "xlsx";

const ExportComponent = ({
  onFetch, // Function to get all data
  data = [],
  headers,
  reportTitle,
  filterSubtitle,
  fileNamePrefix,
  userName,
  totalAmount,
  buttonStyle = {
    width: "100px",
    backgroundColor: "#FF6A00",
    color: "#FFFFFF",
  },
}) => {
  const [loading, setLoading] = useState(false);

  const extractTextFromJSX = (jsx) => {
    if (!jsx || typeof jsx !== "object") return jsx || "N/A";
    if (jsx.props && jsx.props.children) {
      if (typeof jsx.props.children === "string") return jsx.props.children;
      if (Array.isArray(jsx.props.children)) {
        return jsx.props.children
          .map((child) => extractTextFromJSX(child))
          .join(" ");
      }
      return extractTextFromJSX(jsx.props.children);
    }
    return typeof jsx === "string" ? jsx : "N/A";
  };

  const processData = (rawData) => {
    const exportableHeaders = headers.filter((h) => h.exportable !== false);
    const headerLabels = exportableHeaders.map((h) =>
      extractTextFromJSX(h.label || ""),
    );
    const bodyData = rawData.map((row, rowIndex) =>
      exportableHeaders.map((header) => {
        if (header.render) {
          return extractTextFromJSX(header.render(row, rowIndex));
        }
        return row[header.key] ?? "N/A";
      }),
    );
    return { headerLabels, bodyData };
  };

  const handleExport = async (type) => {
    setLoading(true);
    try {
      const allData = onFetch ? await onFetch() : data;
      if (!allData || allData.length === 0) {
        showNotification({
          color: "red",
          title: "Error",
          message: "No data found",
        });
        setLoading(false);
        return;
      }

      const { headerLabels, bodyData } = processData(allData);

      if (type === "PDF") {
        // Create a temporary container for the table
        const container = document.createElement("div");
        container.style.position = "absolute";
        container.style.left = "-9999px";
        container.style.width = "1200px";
        container.style.background = "white";
        container.style.padding = "20px";
        document.body.appendChild(container);

        // Build HTML table with proper styling
        let htmlContent = `
          <div style="font-family: 'Noto Sans Ethiopic', 'Noto Sans', Arial, sans-serif; padding: 20px;">
            <h2 style="text-align: center; color: #333; margin-bottom: 10px;">${reportTitle}</h2>
        `;

        if (userName) {
          htmlContent += `
            <p style="text-align: center; font-size: 14px; margin: 5px 0;">Name: ${userName}</p>
            <p style="text-align: center; font-weight: bold; margin: 10px 0;">Transactions</p>
          `;
        }

        htmlContent += `
          <table style="width: 100%; border-collapse: collapse; margin-top: 20px; font-size: 11px;">
            <thead>
              <tr style="background-color: #FF6A00; color: white;">
                ${headerLabels.map((h) => `<th style="border: 1px solid #ddd; padding: 10px; text-align: left;">${h}</th>`).join("")}
              </tr>
            </thead>
            <tbody>
              ${bodyData
                .map(
                  (row) => `
                <tr>
                  ${row.map((cell) => `<td style="border: 1px solid #ddd; padding: 8px;">${cell}</td>`).join("")}
                </tr>
              `,
                )
                .join("")}
            </tbody>
          </table>
        `;

        if (totalAmount !== undefined && totalAmount !== null) {
          htmlContent += `
            <p style="text-align: right; font-weight: bold; margin-top: 15px; font-size: 13px;">
              Total Amount: ${totalAmount}
            </p>
          `;
        }

        htmlContent += `</div>`;
        container.innerHTML = htmlContent;

        // Use html2canvas to convert to image
        const html2canvas = (await import("html2canvas")).default;

        try {
          const canvas = await html2canvas(container, {
            scale: 2,
            useCORS: true,
            logging: false,
            backgroundColor: "#ffffff",
          });

          document.body.removeChild(container);

          const imgData = canvas.toDataURL("image/png");
          const doc = new jsPDF("l", "mm", "a4");

          const pageWidth = doc.internal.pageSize.getWidth();
          const pageHeight = doc.internal.pageSize.getHeight();

          const imgWidth = pageWidth - 20; // 10mm margin on each side
          const imgHeight = (canvas.height * imgWidth) / canvas.width;

          let heightLeft = imgHeight;
          let position = 10; // Start 10mm from top

          // Add first page
          doc.addImage(imgData, "PNG", 10, position, imgWidth, imgHeight);
          heightLeft -= pageHeight;

          // Add more pages if content is longer than one page
          while (heightLeft > 0) {
            position = heightLeft - imgHeight + 10;
            doc.addPage();
            doc.addImage(imgData, "PNG", 10, position, imgWidth, imgHeight);
            heightLeft -= pageHeight;
          }

          doc.save(`${fileNamePrefix}.pdf`);
        } catch (error) {
          if (document.body.contains(container)) {
            document.body.removeChild(container);
          }
          throw error;
        }
      } else {
        // Excel export
        const rows = [];

        if (userName) {
          rows.push([`Name: ${userName}`]);
          rows.push(["Transactions"]);
          rows.push([]);
        }

        rows.push(headerLabels);
        rows.push(...bodyData);

        if (totalAmount !== undefined && totalAmount !== null) {
          rows.push([]);
          rows.push([
            ...Array(headerLabels.length - 1).fill(""),
            `Total Amount: ${totalAmount}`,
          ]);
        }

        const worksheet = XLSX.utils.aoa_to_sheet(rows);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "Report");
        XLSX.writeFile(workbook, `${fileNamePrefix}.xlsx`);
      }
    } catch (err) {
      showNotification({
        color: "red",
        title: "Export Error",
        message: err.message,
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Menu shadow="md" trigger="hover" openDelay={100} closeDelay={400}>
      <Menu.Target>
        <Button
          style={buttonStyle}
          leftIcon={loading && <Loader size="xs" color="white" />}
        >
          {loading ? "Processing..." : "Export"}
        </Button>
      </Menu.Target>
      <Menu.Dropdown>
        <Menu.Item onClick={() => handleExport("PDF")}>PDF</Menu.Item>
        <Menu.Item onClick={() => handleExport("Excel")}>Excel</Menu.Item>
      </Menu.Dropdown>
    </Menu>
  );
};

export default ExportComponent;
